import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { object, string } from 'yup';
import FieldWrap from '../components/form/FieldWrap';
import Input from '../components/form/Input';
import Label from '../components/form/Label';
import Validation from '../components/form/Validation';
import Icon from '../components/icon/Icon';
import PageWrapper from '../components/layouts/PageWrapper/PageWrapper';
import Button from '../components/ui/Button';
import { editPatient } from '../services/apiService';

import { useApiReload } from '../context/apiRelaod';
import { useAuth } from '../context/authContext';

export type PValues = {
	firstName: string;
	patientId: string;
	lastName: string;
	email: string;
	mobile: string;
};

const validationSchema = object({
	firstName: string()
		.min(3)
		.max(24)
		.matches(/^[A-Za-z\s]+$/, 'Only letters and spaces are allowed')
		.required('Required'),
	lastName: string()
		.min(3)
		.max(24)
		.matches(/^[A-Za-z\s]+$/, 'Only letters and spaces are allowed')
		.required('Required'),
	email: string()
		.email('Invalid email address')
		.notRequired()
		.transform(value => value === '' ? undefined : value),
	mobile: string()
		.notRequired()
		.transform(value => value === '' ? undefined : value)
		.test('phone-format', 'Phone number must be exactly 10 digits', value => {
			return !value || /^\d{10}$/.test(value);
		}),
});

type EditPatientPageProps = {
	patientData?: any;
};

const EditPatientPage = ({ patientData }: EditPatientPageProps) => {
	const { loading, patient_edit } = editPatient();
	const { onLogout } = useAuth();

	const { setReloadPatient, patientID } = useApiReload();
	
	// Use patientData prop if provided, otherwise fall back to patientID from context
	const patientToEdit = patientData || patientID;

	const formik = useFormik<PValues>({
		initialValues: {
			patientId: patientToEdit?.id!,
			firstName: patientToEdit?.firstName!,
			lastName: patientToEdit?.lastName!,
			email: patientToEdit?.email!,
			mobile: patientToEdit?.moblieNo!,
		},
		validationSchema,
		onSubmit: (values: PValues, { setFieldError }) => {
			// Prepare data for API with default values if fields are empty
			const submissionData = {
				...values,
				email: values.email || 'na@na.com', // Default email if empty
				mobile: values.mobile || '0000000000', // Default mobile if empty
			};

			patient_edit(submissionData).subscribe({
				next: (res: any) => {
					formik.resetForm();
					toast.success(res.msg);
					setReloadPatient(true);
				},
				error: (e: any) => {
					if (e?.status === 409) {
						onLogout(e?.status);
					}
					toast.error(e?.data?.msg || e.message);
				},
			});
		},
	});

	return (
		<PageWrapper
			isBeforeLoginRoute={false}
			isProtectedRoute={true}
			className='bg-white dark:bg-inherit'
			name='Add Patient'>
			<div className='flex h-full items-center justify-center'>
				<div className='flex w-full flex-col gap-8 px-5 pb-0 pt-5 md:px-7 md:pt-7'>
					<div>
						<span className='text-4xl font-semibold'>Edit Patient</span>
					</div>
					<form className='flex flex-col gap-4' noValidate>
						<div>
							<Label htmlFor='firstname'>First Name</Label>
							<Validation
								isValid={formik.isValid}
								isTouched={!!formik.touched.firstName}
								invalidFeedback={formik.errors.firstName as string | undefined}>
								<FieldWrap firstSuffix={<Icon icon='HeroUser' className='mx-2' />}>
									<Input
										dimension='lg'
										id='firstName'
										autoComplete='firstName'
										name='firstName'
										placeholder='First Name'
										value={formik.values.firstName}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FieldWrap>
							</Validation>
						</div>
						<div>
							<Label htmlFor='Lastname'>Last Name</Label>
							<Validation
								isValid={formik.isValid}
								isTouched={!!formik.touched.lastName}
								invalidFeedback={formik.errors.lastName as string | undefined}>
								<FieldWrap firstSuffix={<Icon icon='HeroUser' className='mx-2' />}>
									<Input
										dimension='lg'
										id='lastName'
										autoComplete='lastName'
										name='lastName'
										placeholder='Last Name'
										value={formik.values.lastName}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FieldWrap>
							</Validation>
						</div>
						<div>
							<Label htmlFor='email'>Email (Optional)</Label>
							<Validation
								isValid={formik.isValid}
								isTouched={!!formik.touched.email}
								invalidFeedback={formik.errors.email as string | undefined}>
								<FieldWrap
									firstSuffix={<Icon icon='HeroEnvelope' className='mx-2' />}>
									<Input
										dimension='lg'
										id='email'
										autoComplete='email'
										name='email'
										placeholder='Email'
										value={formik.values.email}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FieldWrap>
							</Validation>
						</div>
						<div>
							<Label htmlFor='mobile'>Mobile Number (Optional)</Label>
							<Validation
								isValid={formik.isValid}
								isTouched={!!formik.touched.mobile}
								invalidFeedback={formik.errors.mobile as string | undefined}>
								<FieldWrap firstSuffix={<Icon icon='HeroPhone' className='mx-2' />}>
									<Input
										dimension='lg'
										id='mobile'
										autoComplete='mobile'
										name='mobile'
										placeholder='Mobile'
										value={formik.values.mobile}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FieldWrap>
							</Validation>
						</div>
						<div>
							<Button
								isLoading={loading}
								size='lg'
								variant='solid'
								className='primary-bg w-full font-semibold'
								onClick={() => formik.handleSubmit()}>
								Update
							</Button>
						</div>
					</form>
				</div>
			</div>
		</PageWrapper>
	);
};

export default EditPatientPage;
